import { Box, CircularProgress, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ComputerIcon from '@mui/icons-material/Computer';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HistoryIcon from '@mui/icons-material/History';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { getSecurityEndpointDetailsHref } from 'src/mindtime/config/securityNav';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import type { Column, TableData } from 'src/types/table';
import {
    SecurityDashboardCard,
    SecuritySecurityScoreCard,
} from './shared/securityDashboardCard';
import { SecurityDashboardHeader } from './shared/securityDashboardToolbar';
import { useSecurityDashboardGrid } from './shared/useSecurityDashboardGrid';
import { useWazuhResellerOverviewData } from './shared/useWazuhOverviewData';
import {
    formatImpactPercent,
    resolveOrganizationName,
} from './shared/wazuhDashboardUtils';
import { EdrDateTime, EdrTruncatedText } from './shared/edrPrimitives';

const mutedCellStyle: React.CSSProperties = {
    color: '#718096',
    fontStyle: 'italic',
};

const COLOR_BLUE = '#3182CE';
// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Kpi = {
    id: string;
    label: string;
    value: string;
    subtitle: string;
    icon: ReactNode;
    valueColor?: string;
    subtitleColor?: string;
};

const COLOR_TEXT_DEFAULT = '#1A202C';
const COLOR_TEXT_MUTED = '#718096';
const COLOR_WARNING = '#DD6B20';
const COLOR_DANGER = '#DC2626';

type ClientRow = {
    id: number;
    organization: string;
    type: string;
    mainIssue: string;
    severity: string;
    openIssues: number;
};

type EndpointRow = {
    id: string;
    endpoint: string;
    organization: string;
    issue: string;
    lastSeen: string;
};

type IncidentRow = {
    id: number;
    client: string;
    incident: string;
    endpoint: string;
    time: string;
};

type TaskRow = {
    id: number;
    task: string;
    client: string;
    priority: string;
};

const SEVERITY_PILL_STYLES: Record<string, { color: string; bg: string }> = {
    critical: { color: '#DC2626', bg: '#FEE2E2' },
    high: { color: '#DD6B20', bg: '#FEEBC8' },
    medium: { color: '#B45309', bg: '#FEF3C7' },
    low: { color: '#15803D', bg: '#DCFCE7' },
};

const getSeverityPillStyle = (severity: string) =>
    SEVERITY_PILL_STYLES[severity?.toLowerCase()] ?? SEVERITY_PILL_STYLES.critical;

// ─────────────────────────────────────────────────────────────────────────────
// Presentational helpers
// ─────────────────────────────────────────────────────────────────────────────

const SeverityPill: FC<{ label: string }> = ({ label }) => {
    const { color, bg } = getSeverityPillStyle(label);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 10,
                bgcolor: bg,
                fontSize: 11,
                fontWeight: 600,
                color,
                textTransform: 'capitalize',
            }}
        >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
            {label}
        </Box>
    );
};

const LIST_PAGE_SIZE = 5;

const ListTableCard: FC<{
    icon: ReactNode;
    title: string;
    columns: Column[];
    rows: TableData[];
    loading?: boolean;
    page: number;
    totalRows: number;
    onPageChange?: (page: number) => void;
    style?: React.CSSProperties;
    onRowClick?: (row: TableData) => void;
}> = ({
    icon,
    title,
    columns,
    rows,
    loading = false,
    page,
    totalRows,
    onPageChange,
    style,
    onRowClick,
}) => (
    <SecurityDashboardCard
        title={title}
        icon={icon}
        fitContent
        overflow="hidden"
        style={style}
    >
        <Table
            columns={columns}
            data={rows}
            loading={loading}
            showPagination
            compactPagination
            hideRowsPerPage
            page={page}
            rowsPerPage={LIST_PAGE_SIZE}
            totalRows={totalRows}
            onPageChange={onPageChange}
            onRowClick={onRowClick}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
        />
    </SecurityDashboardCard>
);

const AlignRight: FC<{ children: ReactNode }> = ({ children }) => (
    <Box style={{ textAlign: 'right' }}>{children}</Box>
);

const CLIENT_COLUMNS: Column[] = [
    { id: 'organization', label: 'Organization', render: (row) => <span style={{ fontWeight: 600 }}>{(row as unknown as ClientRow).organization}</span> },
    { id: 'type', label: 'Type', render: (row) => <span style={mutedCellStyle}>{(row as unknown as ClientRow).type}</span> },
    { id: 'mainIssue', label: 'Main Issue', render: (row) => <span style={mutedCellStyle}>{(row as unknown as ClientRow).mainIssue}</span> },
    { id: 'severity', label: 'Severity', render: (row) => <SeverityPill label={(row as unknown as ClientRow).severity} /> },
    { id: 'openIssues', label: 'Open Issues', align: 'right', render: (row) => <AlignRight>{String((row as unknown as ClientRow).openIssues).padStart(2, '0')}</AlignRight> },
];

const ENDPOINT_COLUMNS: Column[] = [
    { id: 'endpoint', label: 'Endpoint', render: (row) => <span style={{ fontWeight: 600 }}>{(row as unknown as EndpointRow).endpoint}</span> },
    { id: 'organization', label: 'Organization', render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointRow).organization}</span> },
    { id: 'issue', label: 'Issue', render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointRow).issue}</span> },
    { id: 'lastSeen', label: 'Last Seen', align: 'right', render: (row) => <AlignRight><EdrDateTime value={(row as unknown as EndpointRow).lastSeen} /></AlignRight> },
];

const INCIDENT_COLUMNS: Column[] = [
    { id: 'client', label: 'Client', render: (row) => (row as unknown as IncidentRow).client },
    {
        id: 'incident',
        label: 'Incident',
        render: (row) => (
            <EdrTruncatedText
                value={(row as unknown as IncidentRow).incident}
                style={mutedCellStyle}
                maxWidth={220}
            />
        ),
    },
    { id: 'endpoint', label: 'Endpoint', render: (row) => <span style={mutedCellStyle}>{(row as unknown as IncidentRow).endpoint}</span> },
    { id: 'time', label: 'Time', align: 'right', render: (row) => <AlignRight><EdrDateTime value={(row as unknown as IncidentRow).time} /></AlignRight> },
];

const TASK_COLUMNS: Column[] = [
    { id: 'task', label: 'Task', render: (row) => <span style={{ fontWeight: 600 }}>{(row as unknown as TaskRow).task}</span> },
    { id: 'client', label: 'Client', render: (row) => <span style={mutedCellStyle}>{(row as unknown as TaskRow).client}</span> },
    { id: 'priority', label: 'Priority', align: 'right', render: (row) => <AlignRight><span style={mutedCellStyle}>{(row as unknown as TaskRow).priority}</span></AlignRight> },
];

const KpiItem: FC<{ kpi: Kpi; loading: boolean; showDivider: boolean }> = ({ kpi, loading, showDivider }) => (
    <Box
        style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '0 20px',
            borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
        }}
    >
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
            <Typography
                style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}
            >
                {kpi.label}
            </Typography>
            {kpi.icon}
        </Box>
        {loading ? (
            <CircularProgress size={22} />
        ) : (
            <Typography style={{ fontSize: 26, fontWeight: 700, color: kpi.valueColor ?? COLOR_TEXT_DEFAULT, lineHeight: 1.1 }}>
                {kpi.value}
            </Typography>
        )}
        <Typography style={{ fontSize: 11, color: kpi.subtitleColor ?? COLOR_TEXT_MUTED, marginTop: 4, fontWeight: kpi.subtitleColor ? 600 : 400 }}>
            {kpi.subtitle}
        </Typography>
    </Box>
);

const getOverviewTableGridStyle = (
    isOverviewStacked: boolean,
    column: number,
    row: number,
): React.CSSProperties => (
    isOverviewStacked
        ? { minWidth: 0 }
        : { minWidth: 0, gridColumn: column, gridRow: row }
);

const getOverviewActivityGridStyle = (
    isOverviewStacked: boolean,
    isOverviewTablet: boolean,
): React.CSSProperties => {
    if (isOverviewStacked) {
        return { minHeight: 420, height: '100%', alignSelf: 'stretch', minWidth: 0 };
    }
    if (isOverviewTablet) {
        return {
            minHeight: 420,
            height: '100%',
            alignSelf: 'stretch',
            minWidth: 0,
            gridColumn: '1 / -1',
            gridRow: 3,
        };
    }
    return {
        minHeight: 420,
        height: '100%',
        alignSelf: 'stretch',
        minWidth: 0,
        gridColumn: 3,
        gridRow: '1 / 3',
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const SecurityOverview: FC = () => {
    const navigate = useNavigate();
    const { getOrganizationName } = useWazuhScope();
    const [attentionPage, setAttentionPage] = useState(0);
    const [endpointsPage, setEndpointsPage] = useState(0);
    const [incidentsPage, setIncidentsPage] = useState(0);
    const overviewData = useWazuhResellerOverviewData(LIST_PAGE_SIZE, {
        attentionPage,
        endpointsPage,
        incidentsPage,
    });
    const {
        overviewMainColumns,
        isOverviewTablet,
        isOverviewStacked,
    } = useSecurityDashboardGrid();

    const overviewPortfolioRowColumns =
        isOverviewStacked || isOverviewTablet
            ? 'minmax(0, 1fr)'
            : overviewMainColumns;
    const [searchQuery, setSearchQuery] = useState('');

    const {
        loading: isOverviewLoading,
        portfolioHealth,
        securityScore,
        organizationsAttention,
        endpointsActionRequired,
        recentIncidents,
    } = overviewData;

    const [clientRows, setClientRows] = useState<ClientRow[]>([]);
    const [endpointRows, setEndpointRows] = useState<EndpointRow[]>([]);
    const [incidentRows, setIncidentRows] = useState<IncidentRow[]>([]);

    useEffect(() => {
        const items = organizationsAttention?.items ?? [];
        let cancelled = false;

        if (!items.length) {
            setClientRows([]);
        } else {
            Promise.all(
                items.map(async (item, index) => ({
                    id: index + 1,
                    organization:
                        item.organizationName ??
                        (await resolveOrganizationName(item.organizationId, getOrganizationName)),
                    type: 'Client',
                    mainIssue: item.mainIssue ?? '—',
                    severity: item.mainIssueSeverity ?? 'Critical',
                    openIssues: item.openIssueCount ?? 0,
                }))
            ).then((rows) => {
                if (!cancelled) setClientRows(rows);
            });
        }

        return () => { cancelled = true; };
    }, [organizationsAttention, getOrganizationName]);

    useEffect(() => {
        const items = endpointsActionRequired?.items ?? [];
        let cancelled = false;

        if (!items.length) {
            setEndpointRows([]);
        } else {
            Promise.all(
                items.map(async (item) => {
                    const orgId = item.organizationId;
                    const organization = orgId
                        ? await resolveOrganizationName(orgId, getOrganizationName)
                        : item.organization ?? '—';
                    return {
                        id: item.systemWazuhAgentId?.trim() ?? '',
                        endpoint: item.endpoint ?? item.name ?? '—',
                        organization,
                        issue: item.issue ?? item.issueLabel ?? '—',
                        lastSeen: item.lastSeen ?? '—',
                    };
                })
            ).then((rows) => {
                if (!cancelled) setEndpointRows(rows);
            });
        }

        return () => { cancelled = true; };
    }, [endpointsActionRequired, getOrganizationName]);

    useEffect(() => {
        const items = recentIncidents?.items ?? [];
        let cancelled = false;

        if (!items.length) {
            setIncidentRows([]);
        } else {
            Promise.all(
                items.map(async (item, index) => {
                    const client = item.client
                        ?? (item.organizationId
                            ? await resolveOrganizationName(item.organizationId, getOrganizationName)
                            : '—');
                    return {
                        id: index + 1,
                        client,
                        incident: item.incident,
                        endpoint: item.endpoint,
                        time: item.time ?? '—',
                    };
                })
            ).then((rows) => {
                if (!cancelled) setIncidentRows(rows);
            });
        }

        return () => { cancelled = true; };
    }, [recentIncidents, getOrganizationName]);

    const impactRows = useMemo(
        () =>
            (securityScore?.impacts ?? []).map((impact, index) => ({
                id: `impact-${index}`,
                label: `${impact.factor}:`,
                impact: formatImpactPercent(impact.impactPercent),
            })),
        [securityScore]
    );

    const myClients = portfolioHealth?.myClients;
    const protectedEndpoints = portfolioHealth?.protectedEndpoints;
    const criticalIncidents = portfolioHealth?.criticalIncidents;
    const openTasks = portfolioHealth?.openTasks;

    const criticalIncidentsTotal = criticalIncidents?.total ?? 0;
    const openTasksTotal = openTasks?.total ?? 0;
    const openTasksHighPriority = openTasks?.highPriority ?? 0;
    const protectedIssues = protectedEndpoints?.issues ?? 0;

    const kpis: Kpi[] = [
        {
            id: 'my_clients',
            label: 'My Clients',
            value: (myClients?.total ?? 0).toLocaleString(),
            subtitle: `${myClients?.healthy ?? 0} Healthy / ${myClients?.needAttention ?? 0} Need Attention`,
            subtitleColor: (myClients?.needAttention ?? 0) > 0 ? COLOR_WARNING : undefined,
            icon: <PeopleOutlineIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'protected_endpoints',
            label: 'Protected Endpoints',
            value: (protectedEndpoints?.total ?? 0).toLocaleString(),
            subtitle: `${(protectedEndpoints?.online ?? 0).toLocaleString()} Online / ${protectedIssues.toLocaleString()} Issues`,
            subtitleColor: protectedIssues > 0 ? COLOR_WARNING : undefined,
            icon: <ComputerIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'critical_incidents',
            label: 'Critical Incidents',
            value: criticalIncidentsTotal.toLocaleString(),
            valueColor: criticalIncidentsTotal > 0 ? COLOR_DANGER : undefined,
            subtitle: `${criticalIncidents?.unresolvedOver48h ?? 0} unresolved > 48 hours`,
            subtitleColor: (criticalIncidents?.unresolvedOver48h ?? 0) > 0 ? COLOR_DANGER : undefined,
            icon: <ReportProblemIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
        {
            id: 'open_tasks',
            label: 'Open Tasks',
            value: openTasksTotal.toLocaleString(),
            valueColor: openTasksTotal > 0 ? COLOR_WARNING : undefined,
            subtitle: `${openTasksHighPriority} High Priority`,
            subtitleColor: openTasksHighPriority > 0 ? COLOR_DANGER : undefined,
            icon: <AssignmentIcon style={{ fontSize: 16, color: '#A0AEC0' }} />,
        },
    ];

    return (
        <>
            <Helmet>
                <title>{getPageTitle('Security — Dashboard')}</title>
            </Helmet>

            <Box
                p={3}
                display="flex"
                flexDirection="column"
                style={{ minHeight: '100%', boxSizing: 'border-box', gap: 16 }}
            >
                <SecurityDashboardHeader
                    searchValue={searchQuery}
                    onSearchChange={setSearchQuery}
                />

                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: overviewPortfolioRowColumns,
                        gap: 16,
                        alignItems: 'stretch',
                        minWidth: 0,
                    }}
                >
                    <SecurityDashboardCard
                        title="Portfolio Health"
                        icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}
                        headerRight={
                            <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>Updated: now</Typography>
                        }
                        style={{
                            minWidth: 0,
                            height: '100%',
                            ...(isOverviewStacked || isOverviewTablet
                                ? {}
                                : { gridColumn: '1 / 3' }),
                        }}
                    >
                        <Box display="flex" sx={{ py: 2 }}>
                            {kpis.map((kpi, index) => (
                                <KpiItem
                                    key={kpi.id}
                                    kpi={kpi}
                                    loading={isOverviewLoading}
                                    showDivider={index > 0}
                                />
                            ))}
                        </Box>
                    </SecurityDashboardCard>

                    <SecuritySecurityScoreCard
                        icon={<ShowChartIcon style={{ fontSize: 18 }} />}
                        score={securityScore ? `${securityScore.score}%` : '—'}
                        comparison={
                            securityScore
                                ? `${securityScore.vsAverage >= 0 ? '+' : ''}${securityScore.vsAverage} vs avg`
                                : '—'
                        }
                        impactRows={impactRows}
                        style={{
                            height: '100%',
                            minWidth: 0,
                            ...(isOverviewStacked || isOverviewTablet
                                ? {}
                                : { gridColumn: 3 }),
                        }}
                    />
                </Box>

                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: overviewMainColumns,
                        gap: 16,
                        alignItems: 'stretch',
                        minWidth: 0,
                    }}
                >
                    <ListTableCard
                        icon={<WarningIcon style={{ fontSize: 18 }} />}
                        title="Clients Requiring Attention"
                        columns={CLIENT_COLUMNS}
                        rows={clientRows as unknown as TableData[]}
                        loading={isOverviewLoading}
                        page={attentionPage}
                        totalRows={organizationsAttention?.totalRecords ?? clientRows.length}
                        onPageChange={setAttentionPage}
                        style={getOverviewTableGridStyle(isOverviewStacked, 1, 1)}
                    />
                    <ListTableCard
                        icon={<ErrorOutlineIcon style={{ fontSize: 18 }} />}
                        title="Endpoints Requiring Immediate Action"
                        columns={ENDPOINT_COLUMNS}
                        rows={endpointRows as unknown as TableData[]}
                        loading={isOverviewLoading}
                        page={endpointsPage}
                        totalRows={endpointsActionRequired?.totalRecords ?? endpointRows.length}
                        onPageChange={setEndpointsPage}
                        style={getOverviewTableGridStyle(isOverviewStacked, 2, 1)}
                        onRowClick={(row) => {
                            const endpointRow = row as unknown as EndpointRow;
                            const systemWazuhAgentId = endpointRow.id?.trim();
                            if (!systemWazuhAgentId) return;
                            navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                                state: { endpointName: endpointRow.endpoint },
                            });
                        }}
                    />
                    <ListTableCard
                        icon={<WhatshotIcon style={{ fontSize: 18 }} />}
                        title="Recent Critical Incidents"
                        columns={INCIDENT_COLUMNS}
                        rows={incidentRows as unknown as TableData[]}
                        loading={isOverviewLoading}
                        page={incidentsPage}
                        totalRows={recentIncidents?.totalRecords ?? incidentRows.length}
                        onPageChange={setIncidentsPage}
                        style={getOverviewTableGridStyle(isOverviewStacked, 1, 2)}
                    />
                    <ListTableCard
                        icon={<AssignmentIcon style={{ fontSize: 18 }} />}
                        title="Open Tasks"
                        columns={TASK_COLUMNS}
                        rows={[] as TableData[]}
                        page={0}
                        totalRows={0}
                        style={getOverviewTableGridStyle(isOverviewStacked, 2, 2)}
                    />

                    <SecurityDashboardCard
                        title="Recent Client Activity"
                        icon={<HistoryIcon style={{ fontSize: 18 }} />}
                        headerRight={<TuneIcon style={{ fontSize: 16, color: '#A0AEC0', cursor: 'pointer' }} />}
                        style={getOverviewActivityGridStyle(isOverviewStacked, isOverviewTablet)}
                        footer={(
                            <Box display="flex" alignItems="center" style={{ gap: 4, cursor: 'pointer' }}>
                                <ImportExportIcon style={{ fontSize: 14, color: COLOR_BLUE }} />
                                <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.3px', color: COLOR_BLUE }}>
                                    VIEW FULL AUDIT LOG
                                </Typography>
                            </Box>
                        )}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            style={{ flex: 1, minHeight: 200, padding: 24 }}
                        >
                            <Typography
                                style={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: 12,
                                    color: '#94A3B8',
                                }}
                            >
                                Not available via API
                            </Typography>
                        </Box>
                    </SecurityDashboardCard>
                </Box>
            </Box>
        </>
    );
};

export default SecurityOverview;
